// common
import i18n, { getLocale, setI18nLanguage, setLanguageCookies } from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;

// interfaces
import { ILanguages } from "@/interfaces/_shared/user";
import { LanguagesType } from "@/types/app";
import dayjs from "dayjs";
import ComplianceModule from "@/store/modules/compliance";
import POSConfigModule from "@/store/modules/pos-config";
import { setElementPlusWeekStart } from "./dates";

export function setLanguage(lang: string) {
  setLanguageCookies(lang);
  setI18nLanguage(i18n, lang);
  dayjs.locale(lang);
  setElementPlusWeekStart(lang);
  // Keep fiscal events in the operator's language. The compliance object
  // captures the language at construction, so it has to be told about the
  // change; countries whose implementation does not support it no-op.
  ComplianceModule.compliance?.setLanguage(lang.toUpperCase());
}

export function getLanguage(): LanguagesType {
  return getLocale();
}

export function getLanguageId(language: string): number {
  const languageInt: { [language: string]: number } = {
    fr: 1,
    en: 2,
    de: 3,
    it: 4,
    es: 7,
  };
  return languageInt[language] || languageInt.en;
}

/**
 *  Language list
 */
export function showLanguagesList(): ILanguages[] {
  const languages = [
    {
      id: 1,
      name: t("common.languages.french"),
      code: "fr",
      local: "fr-FR",
      native_name: "Français",
    },
    {
      id: 2,
      name: t("common.languages.english"),
      code: "en",
      local: "en-US",
      native_name: "English",
    },
    {
      id: 3,
      name: t("common.languages.german"),
      code: "de",
      local: "de-DE",
      native_name: "Deutsch",
    },
    {
      id: 4,
      name: t("common.languages.italian"),
      code: "it",
      local: "it-IT",
      native_name: "Italiano",
    },
    {
      id: 7,
      name: t("common.languages.spanish"),
      code: "es",
      local: "es-ES",
      native_name: "Español",
    },
  ];

  return languages;
}

/**
 * Get lang id by code
 *
 * @param {string} code: lang code
 * @returns {number | string}
 */
export function convertCodeToIdLang(code: string): number | string {
  const lang: any = showLanguagesList().find(
    (item: any) => item.code == code
  ) || { id: "" };

  return lang.id;
}

/**
 * Get lang code by id
 *
 * @param {number} id: lang id
 * @returns {string}
 */
export function convertIdToCodeLang(id: number): LanguagesType {
  const lang: any = showLanguagesList().find((item: any) => item.id == id) || {
    code: "",
  };

  return lang.code;
}

/**
 * Get language name by code
 */
export function convertCodeToNameLang(code: string): number | string {
  const lang: any = showLanguagesList().find(
    (item: any) => item.code == code
  ) || { name: "" };

  return lang.name;
}

/**
 * Get language name by code
 */
export function convertIdToNameLang(id: number): string {
  const lang: any = showLanguagesList().find((item: any) => item.id == id) || {
    name: "",
  };

  return lang.name;
}
